/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { XMLParser } from 'fast-xml-parser';

/**
 * @import { MediaFile, Linear, Creative, Impression, Wrapper, InLine, VAST, TrackingEvent, AdInline, AdWrapper } from "."
 */

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '',
  trimValues: true,
  parseTagValue: true
});

/**
 * @template T
 * @param {T | T[] | undefined} value
 * @returns {T[]}
 */
function ensureArray(value) {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

/**
 * @param {any} node
 * @returns {string}
 */
function getText(node) {
  let text = '';
  if (typeof node === 'string') {
    text = node;
  }
  if (node?.['#text']) {
    text = node['#text'];
  }
  return cleanXmlString(text);
}

/**
 * @param {string} xml
 * @returns {VAST}
 */
export function parseVAST(xml) {
  const json = parser.parse(cleanXmlString(xml));

  const vastNode = json.VAST;
  if (!vastNode) {
    throw new Error('Invalid VAST XML: Missing VAST root');
  }

  return {
    version: vastNode.version,
    Error: ensureArray(vastNode.Error).map(getText),
    Ad: ensureArray(vastNode.Ad).map(parseAd)
  };
}

/**
 * @param {any} node
 * @returns {AdInline | AdWrapper}
 */
function parseAd(node) {
  const ad = {
    id: node.id,
    sequence: node.sequence ? Number(node.sequence) : undefined,
    conditionalAd: node.conditionalAd === true
  };
  if (node.InLine) {
    return { ...ad, type: 'inline', InLine: parseInline(node.InLine) };
  } else if (node.Wrapper) {
    return { ...ad, type: 'wrapper', Wrapper: parseWrapper(node.Wrapper) };
  }
  throw new Error('Invalid VAST XML: Ad must contain either InLine or Wrapper');
}

/**
 * @param {any} node
 * @returns {InLine}
 */
function parseInline(node) {
  return {
    AdTitle: getText(node.AdTitle),
    Error: ensureArray(node.Error).map(getText),
    Impression: ensureArray(node.Impression).map(parseImpression),
    Creatives: node.Creatives
      ? ensureArray(node.Creatives.Creative).map(parseCreative)
      : undefined
  };
}

/**
 * @param {any} node
 * @returns {Wrapper}
 */
function parseWrapper(node) {
  return {
    AllowMultipleAds: node.allowMultipleAds === 'true',
    VASTAdTagURI: getText(node.VASTAdTagURI),
    Error: ensureArray(node.Error).map(getText),
    Impression: ensureArray(node.Impression).map(parseImpression)
  };
}

/**
 * @param {any} node
 * @returns {Impression}
 */
function parseImpression(node) {
  return getText(node);
}

/**
 * @param {any} node
 * @returns {Creative}
 */
function parseCreative(node) {
  return {
    id: node.id,
    sequence: node.sequence ? Number(node.sequence) : undefined,
    Linear: node.Linear ? parseLinear(node.Linear) : undefined
  };
}

/**
 * @param {any} node
 * @returns {Linear}
 */
function parseLinear(node) {
  return {
    Duration: getText(node.Duration),
    MediaFiles: node.MediaFiles
      ? ensureArray(node.MediaFiles.MediaFile).map(parseMediaFile)
      : undefined,
    TrackingEvents: node.TrackingEvents
      ? parseTrackingEvents(node.TrackingEvents)
      : undefined
  };
}

/**
 * @param {any} node
 * @returns {Map<TrackingEvent, string>}
 */
function parseTrackingEvents(node) {
  return ensureArray(node.Tracking).reduce((acc, tracking) => {
    acc.set(tracking.event, getText(tracking));
    return acc;
  }, /** @type {Map<TrackingEvent, string>} */ (new Map()));
}

/**
 * @param {any} node
 * @returns {MediaFile}
 */
function parseMediaFile(node) {
  return {
    id: node.id,
    delivery: node.delivery,
    type: node.type,
    bitrate: node.bitrate ? Number(node.bitrate) : undefined,
    width: node.width ? Number(node.width) : undefined,
    height: node.height ? Number(node.height) : undefined,
    uri: getText(node)
  };
}

/**
 * @param {string} xml
 * @returns {string}
 */
function cleanXmlString(xml) {
  // eslint-disable-next-line no-control-regex
  return xml.replace(/^[\uFEFF\u200B\u0000\s]+/, '').trim();
}
